import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { AppConfig } from '@tellvo/setup-protocol';
import { Button, Chip, Switch, type Tone } from './controls.js';
import { Editor } from './Editor.js';
import { t, type Key } from './i18n/index.js';
import {
  IconAlert, IconBell, IconBellOff, IconBrowser, IconCheck, IconChevronRight, IconGrip, IconInfo, IconLock,
  IconMoon, IconPalette, IconPlus, IconShield, IconSmartphone, IconSparkle, IconTerminal, IconUnlock,
} from './icons.js';
import { initials, number } from './format.js';
import { Plans } from './Plans.js';
import { noRoom, pushMissing, pushRow, ringsWhenClosed } from './push.js';
import { moved, useDragOrder, type DragHandles } from './reorder.js';
import type { Connection } from './session.js';
import type { Notice, SetupState, SetupStore } from './store.js';
import type { AccountMark, View } from './sync.js';

type Entry = AppConfig['accounts'][number];

/**
 * The console (variant C, tellvo-voip-client#96): the state of things first — one sentence,
 * three numbers and the live link to the iPhone — then the lines as cards, the plan, the app's
 * own settings and the SIP log. A line opens in a sheet on the right. The page scrolls as one;
 * everything that depends on the iPhone being there sits in a slot of fixed size, so nothing
 * moves when it comes and goes.
 */
export function Console({ store, state }: { store: SetupStore; state: SetupState }) {
  const view = store.view;
  const online = state.phase.kind === 'paired';
  const [selected, setSelected] = useState<string | null>(null);
  const [newId, setNewId] = useState<string | null>(null);

  // An account that went away (deleted on the phone) closes its editor.
  useEffect(() => {
    if (selected && selected !== newId && view && !view.config.accounts.some((a) => idOf(a) === selected)) setSelected(null);
  }, [selected, newId, view]);

  if (!view) return <main className="console"><div className="col"><p className="field__hint">{t('connect.waitingPhone')}</p></div></main>;

  const open = (id: string, fresh = false) => {
    store.clearNotice();
    setNewId(fresh ? id : null);
    setSelected(id);
  };
  const add = () => open(crypto.randomUUID(), true);
  const close = () => { setSelected(null); setNewId(null); };
  const entry = selected ? view.config.accounts.find((a) => idOf(a) === selected) : undefined;
  const accounts = view.config.accounts;
  const order = useOrder(store, accounts.map(idOf));

  return (
    <main className="console">
      <Summary view={view} store={store} online={online} connection={state.connection} notice={state.notice} onOpen={open} onAdd={add} />
      <div className="col console__body">
        <section className="sec" aria-labelledby="h-lines">
          <div className="sec__head">
            <div><h2 id="h-lines">{t('accounts.title')}</h2><p className="field__hint">{t('accounts.lead')}</p></div>
            {accounts.length ? <Button onClick={add}><IconPlus />{t('accounts.add')}</Button> : null}
          </div>
          {accounts.length ? (
            <div className={`lines${order.drag.listClass}`} ref={order.drag.listRef}>
              {accounts.map((a, i) => <LineCard key={idOf(a)} entry={a} index={i} mark={view.marks.get(idOf(a))} store={store} online={online}
                push={view.config.features.pushIncoming} onOpen={() => open(idOf(a))} drag={order.drag} onMove={order.move} />)}
              <button type="button" className="add-card" onClick={add}>
                <span className="add-card__plus"><IconPlus /></span><b>{t('accounts.addFirst')}</b><span className="field__hint">{t('accounts.addHint')}</span>
              </button>
              <p id="lines-hint" className="visually-hidden">{t('accounts.moveHint')}</p>
              <p className="visually-hidden" role="status" aria-live="polite">{order.said}</p>
            </div>
          ) : <FirstSteps onAdd={add} />}
        </section>

        {accounts.length ? (
          <>
            {offersPlans(view.config) ? <Plans config={view.config} store={store} online={online} notice={state.notice} /> : null}
            <div className="two">
              <AppAppearance config={view.config} store={store} />
              <Features config={view.config} store={store} />
            </div>
            <Diagnostics store={store} online={online} log={state.log} />
          </>
        ) : null}
        <p className="trust"><IconShield size="sm" />{t('pair.privacy')}</p>
      </div>

      {selected ? (
        <Sheet onClose={close}>
          <Editor key={selected} store={store} id={selected} entry={entry} isNew={!entry} online={online}
            feature={view.config.features.pushIncoming} mark={view.marks.get(selected)} check={state.check} checking={state.checking} onDone={close} />
        </Sheet>
      ) : null}
    </main>
  );
}

const idOf = (entry: Entry): string => String(entry.account['id'] ?? '');

/**
 * The order of the cards is the order the lines dial in (#12). A drop or Alt+arrows sends the
 * whole new order once; the cards stand in it, marked, until the phone's snapshot shows it.
 * Only a line the phone has can be ordered among its own. The card moved from the keyboard
 * keeps the focus, and a status line says where it went.
 */
function useOrder(store: SetupStore, ids: string[]) {
  const [said, setSaid] = useState('');
  const refocus = useRef<string | null>(null);
  const send = (next: string[], id: string) => {
    store.send({ kind: 'reorder', accountIds: next });
    setSaid(t('accounts.moved', { n: next.indexOf(id) + 1, total: next.length }));
  };
  const drag = useDragOrder(ids, (id) => store.phoneHas(id), send);
  const move = (id: string, by: -1 | 1) => {
    const from = ids.indexOf(id);
    const to = from + by;
    if (from < 0 || to < 0 || to >= ids.length || !store.phoneHas(id)) return;
    refocus.current = id;
    send(moved(ids, from, to), id);
  };
  useEffect(() => {
    if (!refocus.current) return;
    document.querySelector<HTMLElement>(`[data-id="${refocus.current}"]`)?.focus();
    refocus.current = null;
  });
  return { drag, move, said };
}
const nameOf = (entry: Entry): string => String(entry.account['displayName'] ?? '') || String(entry.account['domain'] ?? '');

/** One word on the plate; colour only for working, waiting, attention. */
function accountState(entry: Entry, mark: AccountMark | undefined, onPhone: boolean, online: boolean): { tone: Tone; label: Key; line?: Key } {
  if (mark?.kind === 'failed') return { tone: 'bad', label: 'state.error', line: 'line.failed' };
  if (mark?.kind === 'deleting') return { tone: 'warn', label: 'state.waiting', line: 'line.deleting' };
  if (!onPhone) return { tone: 'warn', label: 'state.waiting', line: 'line.notOnPhone' };
  if (mark?.kind === 'pending') return { tone: 'warn', label: 'state.waiting', line: online ? 'line.pendingOnline' : 'line.pendingAway' };
  if (entry.account['enabled'] === false) return { tone: 'neutral', label: 'state.off', line: 'line.off' };
  switch (entry.registration.state) {
    case 'registered': return { tone: 'success', label: 'state.working' };
    case 'registering': return { tone: 'warn', label: 'state.connecting' };
    case 'failed': return { tone: 'bad', label: 'state.error', line: 'line.error' };
    default: return { tone: 'neutral', label: 'state.idle' };
  }
}

// MARK: summary

function Summary({ view, store, online, connection, notice, onOpen, onAdd }: {
  view: View; store: SetupStore; online: boolean; connection: Connection; notice: Notice | null;
  onOpen: (id: string) => void; onAdd: () => void;
}) {
  const accounts = view.config.accounts;
  const states = accounts.map((a) => ({ a, s: accountState(a, view.marks.get(idOf(a)), store.phoneHas(idOf(a)), online) }));
  const ready = states.filter((x) => x.s.tone === 'success').length;
  const broken = states.filter((x) => x.s.tone === 'bad');
  const push = view.config.features.pushIncoming;
  const quiet = states.filter(({ a, s }) => s.tone === 'success' && !ringsWhenClosed(a.push, a.pushSwitch, push)).length;
  const first = broken[0];
  const app = view.config.app;

  const title = !accounts.length ? t('console.summary.empty')
    : first ? t('console.summary.attention', { ready, total: accounts.length, name: nameOf(first.a) })
      : ready === accounts.length ? t('console.summary.all') : t('console.summary.some', { ready, total: accounts.length });
  const sub = !accounts.length ? t('console.sub.empty') : first ? t('console.sub.attention') : t('console.sub.ok');

  return (
    <section className="summary"><div className="col summary__in">
      <div className="summary__text">
        <span className="eyebrow">{t('sync.device', { device: app.deviceName ?? 'iPhone', version: app.version })}</span>
        <h1>{title}</h1>
        <p className="summary__sub">{sub}</p>
        <div className="summary__actions">
          {!accounts.length ? <Button kind="primary" onClick={onAdd}><IconPlus />{t('accounts.addFirst')}</Button>
            : first ? <Button onClick={() => onOpen(idOf(first.a))}>{t('console.fix', { name: nameOf(first.a) })}<IconChevronRight size="sm" /></Button> : null}
        </div>
        {accounts.length ? (
          <dl className="stats">
            <div><dt>{t('console.stat.ready')}</dt><dd className="num">{ready}</dd></div>
            <div><dt>{t('console.stat.attention')}</dt><dd className="num">{broken.length}</dd></div>
            <div><dt>{t('console.stat.quiet')}</dt><dd className="num">{quiet}</dd></div>
          </dl>
        ) : null}
        <NoticeLine notice={isPlanAnswer(notice) ? null : notice} />
      </div>
      <LinkCard device={app.deviceName ?? 'iPhone'} online={online} connection={connection} pending={view.pending} onRetry={() => store.retry()} />
    </div></section>
  );
}

/** What the phone said last (saved, declined, confirm on the iPhone). Always there, so nothing moves. */
/** The phone's answer to "open the plans" shows by the plan block's button, not up here. */
const isPlanAnswer = (notice: Notice | null): boolean =>
  notice !== null && 'key' in notice && (notice.key === 'notice.plansOpen' || notice.key === 'notice.plansClosed');

function NoticeLine({ notice }: { notice: Notice | null }) {
  const text = !notice ? '' : 'text' in notice ? notice.text : t(notice.key);
  const tone = notice?.tone === 'warn' ? 'warn' : notice?.tone === 'bad' ? 'bad' : 'ok';
  const waiting = notice !== null && 'waiting' in notice;
  return <div className={`note note--${tone} summary__notice${waiting ? ' attention' : ''}`} data-empty={!notice} aria-live="polite">{text || ' '}</div>;
}

/**
 * This browser ⟷ the iPhone, encrypted end to end. The line under it says how the link is,
 * in a slot two lines high whatever it says.
 */
function LinkCard({ device, online, connection, pending, onRetry }: {
  device: string; online: boolean; connection: Connection; pending: number; onRetry: () => void;
}) {
  const state = connection === 'offline' ? 'off' : connection === 'reconnecting' ? 'lost' : online ? 'live' : 'away';
  const [icon, text]: [ReactNode, string] = state === 'live' ? [<IconCheck size="sm" />, t('link.live')]
    : state === 'away' ? [<IconMoon size="sm" />, pending ? t('link.waiting', { count: pending }) : t('link.away')]
      : state === 'lost' ? [<span className="spinner spinner--xs" aria-hidden="true" />, t('link.reconnecting')]
        : [<IconAlert size="sm" />, t('link.offline')];
  return (
    <div className={`card link link--${state}`}>
      <div className="link__row">
        <div className="link__end"><span className="link__tile"><IconBrowser /></span><b>{t('link.browser')}</b></div>
        <div className="link__wire" aria-hidden="true" />
        <div className="link__end"><span className="link__tile link__tile--phone"><IconSmartphone /></span><b>{device}</b></div>
        <div className="link__badge"><IconLock size="xs" />{t('link.encrypted')}</div>
      </div>
      <div className="link__foot" role="status">
        <span className="link__icon">{icon}</span><span>{text}</span>
        {state === 'off' ? <Button kind="sm" onClick={onRetry}>{t('action.retry')}</Button> : null}
      </div>
    </div>
  );
}

// MARK: lines

function LineCard({ entry, index, mark, store, online, push, onOpen, drag, onMove }: {
  entry: Entry; index: number; mark: AccountMark | undefined; store: SetupStore; online: boolean;
  push: AppConfig['features']['pushIncoming']; onOpen: () => void; drag: DragHandles; onMove: (id: string, by: -1 | 1) => void;
}) {
  const id = idOf(entry);
  const name = nameOf(entry);
  const a = entry.account;
  const onPhone = store.phoneHas(id);
  const s = accountState(entry, mark, onPhone, online);
  const { className: dragClass, ...dragProps } = drag.cardProps(id, index);
  const onKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowLeft')) { e.preventDefault(); onMove(id, -1); return; }
    if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowRight')) { e.preventDefault(); onMove(id, 1); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
  };
  const enabled = a['enabled'] !== false;
  const broken = s.tone === 'bad';
  const srtp = String(a['srtp'] ?? 'disabled');
  // Two facts, always two: what rings and whether calls are encrypted. A change that waits for
  // the phone takes the first slot, so the card keeps its height.
  // The push switch (#16, variant B) sits on that first fact, when the phone has one per account.
  const row = pushRow(entry.pushSwitch, push);
  const sw = entry.pushSwitch;
  const noPlace = sw && !sw.available && (sw.reason === 'noRoom' || sw.reason === 'otherDevices') ? noRoom(sw, 'line.pushNoRoom', 'line.pushNoRoomBare') : null;
  const ring: [ReactNode, string, string] = s.line && s.tone === 'warn' ? [<IconInfo size="sm" />, t(s.line), 'fact--warn']
    : !enabled ? [<IconBellOff size="sm" />, t('fact.off'), '']
      : broken ? [<IconAlert size="sm" />, t(s.line ?? 'line.error'), 'fact--bad']
        : noPlace ? [<IconBellOff size="sm" />, t(noPlace.key, noPlace.params), 'fact--warn']
          : !push.available || !push.enabled ? [<IconBell size="sm" />, t('fact.open'), '']
            : entry.push?.state === 'doNotDisturb' ? [<IconMoon size="sm" />, t('fact.dnd'), '']
              : sw && !sw.on ? [<IconBell size="sm" />, t('fact.open'), '']
                : pushMissing(entry.push) ? [<IconBellOff size="sm" />, t('line.pushMissing'), 'fact--warn']
                  : [<IconBell size="sm" />, t('fact.closed'), ''];
  const ringControl = row.kind === 'paid' ? <span className="chip chip--plain">{t('features.paid')}</span>
    : row.kind === 'switch' ? <Switch on={row.on} disabled={row.disabled} label={t('accounts.pushToggle', { name })}
      onChange={(on) => store.send({ kind: 'accountPush', accountId: id, enabled: on })} /> : null;
  const enc: [ReactNode, string] = srtp === 'disabled' ? [<IconUnlock size="sm" />, t('fact.enc.off')]
    : [<IconLock size="sm" />, t(srtp === 'mandatory' ? 'fact.enc.mandatory' : 'fact.enc.optional')];
  return (
    <div className={`card line${enabled ? '' : ' line--off'}${mark?.kind === 'deleting' ? ' line--deleting' : ''}${dragClass}`} role="button" tabIndex={0}
      data-id={id} aria-label={t('accounts.edit', { name })} aria-describedby={onPhone ? 'lines-hint' : undefined} onClick={onOpen} onKeyDown={onKey} {...dragProps}>
      <div className="line__top">
        <span className="avatar" aria-hidden="true">{initials(name)}</span>
        {onPhone ? <span className="grip" data-grip title={t('accounts.drag')} aria-hidden="true"><IconGrip /></span> : null}
        <Chip tone={s.tone}>{t(s.label)}</Chip>
      </div>
      <div className="line__who">
        <span className="line__name">{name}</span>
        <span className="line__num num">{number(String(a['username'] ?? ''))}</span>
        <span className="line__host">{String(a['domain'] ?? '')} · {String(a['transport'] ?? 'udp').toUpperCase()}</span>
      </div>
      <div className="facts">
        <span className={`fact ${ring[2]}${ringControl ? ' fact--switch' : ''}`}>{ring[0]}<span>{ring[1]}</span>{ringControl}</span>
        <span className="fact">{enc[0]}<span>{enc[1]}</span></span>
      </div>
      <div className="line__foot">
        {broken && enabled ? <span className="button button--sm" aria-hidden="true">{t('accounts.fix')}</span> : <span className="field__hint">{t('accounts.active')}</span>}
        <Switch on={enabled} label={t('accounts.toggle', { name })} disabled={mark?.kind === 'deleting'}
          onChange={(on) => store.send({ kind: 'enable', accountId: id, enabled: on })} />
      </div>
    </div>
  );
}

function FirstSteps({ onAdd }: { onAdd: () => void }) {
  const steps: [Key, Key][] = [['start.1.title', 'start.1.text'], ['start.2.title', 'start.2.text'], ['start.3.title', 'start.3.text']];
  return (
    <ol className="steps">
      {steps.map(([title, text], i) => (
        <li key={title} className={`card step${i === 0 ? ' step--now' : ''}`}>
          <span className="step-n">{i + 1}</span><b>{t(title)}</b><span className="field__hint">{t(text)}</span>
          {i === 0 ? <div><Button kind="primary" onClick={onAdd}><IconPlus />{t('accounts.addFirst')}</Button></div> : null}
        </li>
      ))}
    </ol>
  );
}

// MARK: plan

/**
 * The plan block shows on Free only: an app that sends the plan says so; an older one says it by
 * having no incoming calls while the app is closed.
 */
function offersPlans(config: AppConfig): boolean {
  return config.plan ? config.plan.tier === 'free' : !config.features.pushIncoming.available;
}

// MARK: app on the iPhone

function AppAppearance({ config, store }: { config: AppConfig; store: SetupStore }) {
  const options: { value: AppConfig['settings']['appearance']; label: Key }[] = [
    { value: 'system', label: 'app.appearance.system' }, { value: 'light', label: 'app.appearance.light' }, { value: 'dark', label: 'app.appearance.dark' },
  ];
  return (
    <section className="card panel" aria-labelledby="h-app">
      <div className="panel__head"><IconPalette /><h3 id="h-app">{t('app.appearance')}</h3></div>
      <div className="looks" role="radiogroup" aria-label={t('app.appearance')}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={config.settings.appearance === o.value} className="look"
            onClick={() => store.send({ kind: 'settings', patch: { appearance: o.value } })}>
            <span className="look__shot" aria-hidden="true">{o.value === 'system' ? <span className="look__split"><Shot kind="light" /><Shot kind="dark" /></span> : <Shot kind={o.value} />}</span>
            {t(o.label)}
          </button>
        ))}
      </div>
      <p className="field__hint">{t('app.appearance.hint')}</p>
    </section>
  );
}

/** The app's keypad in miniature, in its own fixed colours: what the phone will look like. */
function Shot({ kind }: { kind: 'light' | 'dark' }) {
  return <span className={`shot shot--${kind}`}><i className="shot__bar" /><span className="shot__keys">{Array.from({ length: 6 }, (_, i) => <i key={i} />)}</span><i className="shot__call" /></span>;
}

function Features({ config, store }: { config: AppConfig; store: SetupStore }) {
  const items = [
    { key: 'pushIncoming', name: t('features.pushIncoming'), hint: t('features.pushIncoming.hint'), unavailable: t('features.pushIncoming.unavailable') },
    { key: 'sms', name: t('features.sms'), hint: t('features.sms.hint'), unavailable: t('features.sms.unavailable') },
  ] as const;
  return (
    <section className="card panel" aria-labelledby="h-features">
      <div className="panel__head"><IconSparkle /><h3 id="h-features">{t('features.section')}</h3></div>
      {items.map((item) => {
        const feature = config.features[item.key];
        return (
          <div key={item.key} className="setrow">
            <div className="setrow__what"><b>{item.name}</b><span className="field__hint">{feature.available ? item.hint : `${item.hint} ${item.unavailable}`}</span></div>
            {/* A feature that cannot be turned on has no switch, only a word on why. */}
            {feature.available
              ? <Switch on={feature.enabled} label={t('features.toggle', { name: item.name })} onChange={(enabled) => store.send({ kind: 'feature', key: item.key, enabled })} />
              : <span className="chip chip--plain">{t(item.key === 'pushIncoming' ? 'features.paid' : 'features.soon')}</span>}
          </div>
        );
      })}
    </section>
  );
}

function Diagnostics({ store, online, log }: { store: SetupStore; online: boolean; log: string[] | null }) {
  const pre = useRef<HTMLPreElement>(null);
  const copy = () => { void navigator.clipboard?.writeText(log?.join('\n') ?? '').catch(() => {}); };
  return (
    <section className="card diag" aria-labelledby="h-diag">
      <div className="diag__head">
        <IconTerminal />
        <div><h3 id="h-diag">{t('diag.log')}</h3><p className="field__hint">{t('diag.log.hint')}</p></div>
        <div className="form-foot">
          {log ? <Button kind="ghost" onClick={copy}>{t('action.copy')}</Button> : null}
          <Button disabled={!online} onClick={() => store.requestLog()}>{t('action.showLog')}</Button>
        </div>
      </div>
      {log ? <pre className="log" ref={pre} tabIndex={0}>{log.join('\n') || t('diag.empty')}</pre> : null}
    </section>
  );
}

// MARK: sheet

/**
 * The editor beside the console. It closes on its close button, Cancel, Esc or a click on the
 * scrim around it, all alike.
 */
function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', key); document.body.style.overflow = overflow; };
  }, [onClose]);
  return (
    <>
      <div className="scrim" aria-hidden="true" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="editor-title">{children}</div>
    </>
  );
}
